import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  AutoAwesome as GenerateIcon,
  Shuffle as ShuffleIcon,
  Delete as DeleteIcon,
  ThumbUp,
  ThumbDown,
  ReportProblem,
} from '@mui/icons-material';
import {
  pickRandomImageTestRecipes,
  generateImageTest,
  listImageTests,
  rateImageTest,
  deleteImageTest,
  listFluxModels,
  ImageTest as ImageTestRow,
  FluxModel,
} from '../adminApi';

const ImageTest: React.FC = () => {
  const [tests, setTests] = useState<ImageTestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<Set<number>>(new Set());
  const [batchSize, setBatchSize] = useState(10);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<string | null>(null);
  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [manualRecipeId, setManualRecipeId] = useState('');

  const [models, setModels] = useState<FluxModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const selectedModelInfo = models.find(m => m.key === selectedModel) || null;

  const fetchTests = async () => {
    setLoading(true);
    try {
      const data = await listImageTests();
      setTests(data);
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Failed to load tests: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTests();
    listFluxModels()
      .then(({ models, default: def }) => {
        setModels(models);
        setSelectedModel(def);
      })
      .catch(err => setMessage({ severity: 'error', text: `Failed to load model list: ${err.message}` }));
  }, []);

  const runBatch = async () => {
    setBatchRunning(true);
    setBatchProgress('Picking random recipes…');
    try {
      const recipes = await pickRandomImageTestRecipes(batchSize);
      let done = 0;
      for (const r of recipes) {
        setBatchProgress(`Generating ${done + 1}/${recipes.length}: ${r.name} (${selectedModel})…`);
        try {
          await generateImageTest(r.recipe_id, selectedModel || undefined);
        } catch (err: any) {
          console.warn(`Failed to generate for ${r.name}:`, err.message);
        }
        done++;
      }
      setBatchProgress(null);
      setMessage({ severity: 'success', text: `Generated ${done} image(s). Refresh below to see results.` });
      await fetchTests();
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Batch failed: ${err.message}` });
    } finally {
      setBatchRunning(false);
      setBatchProgress(null);
    }
  };

  const generateOne = async (recipeId: number) => {
    setGenerating(prev => new Set(prev).add(recipeId));
    try {
      await generateImageTest(recipeId, selectedModel || undefined);
      await fetchTests();
      setMessage({ severity: 'success', text: `Generated image for recipe ${recipeId} (${selectedModel})` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Generation failed: ${err.message}` });
    } finally {
      setGenerating(prev => {
        const next = new Set(prev);
        next.delete(recipeId);
        return next;
      });
    }
  };

  const handleRate = async (testId: number, rating: ImageTestRow['rating']) => {
    try {
      const updated = await rateImageTest(testId, rating);
      setTests(prev => prev.map(t => t.test_id === testId ? updated : t));
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Rating failed: ${err.message}` });
    }
  };

  const handleDelete = async (testId: number) => {
    if (!window.confirm('Delete this test row?')) return;
    try {
      await deleteImageTest(testId);
      setTests(prev => prev.filter(t => t.test_id !== testId));
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Delete failed: ${err.message}` });
    }
  };

  const stats = {
    total: tests.length,
    new_better: tests.filter(t => t.rating === 'new_better').length,
    current_better: tests.filter(t => t.rating === 'current_better').length,
    both_bad: tests.filter(t => t.rating === 'both_bad').length,
    unrated: tests.filter(t => t.rating === null).length,
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Image Quality Test</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Compare Flux Pro 1.1 (via fal.ai) against the current DALL-E images. Pick recipes,
        generate new versions, rate side-by-side. Costs ~$0.025 per Flux image.
      </Typography>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Run a batch</Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 240 }} disabled={batchRunning || models.length === 0}>
            <InputLabel>Model</InputLabel>
            <Select value={selectedModel} label="Model" onChange={e => setSelectedModel(e.target.value)}>
              {models.map(m => (
                <MenuItem key={m.key} value={m.key}>
                  {m.label} — ${m.costPerImage.toFixed(3)}/img
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedModelInfo && (
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
              {selectedModelInfo.note}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <TextField
            type="number"
            label="Batch size"
            size="small"
            value={batchSize}
            onChange={e => setBatchSize(Math.max(1, Math.min(25, parseInt(e.target.value) || 1)))}
            sx={{ width: 120 }}
            disabled={batchRunning}
          />
          <Button
            variant="contained"
            startIcon={batchRunning ? <CircularProgress size={16} /> : <ShuffleIcon />}
            onClick={runBatch}
            disabled={batchRunning || !selectedModel}
          >
            Pick {batchSize} random + generate
          </Button>
          {selectedModelInfo && (
            <Chip
              size="small"
              variant="outlined"
              label={`Est: $${(batchSize * selectedModelInfo.costPerImage * 1.5).toFixed(2)} (assumes ~1.5 attempts/recipe)`}
            />
          )}
          {batchProgress && (
            <Typography variant="body2" color="text.secondary">{batchProgress}</Typography>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>Or generate for a specific recipe ID</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Recipe ID"
            size="small"
            value={manualRecipeId}
            onChange={e => setManualRecipeId(e.target.value)}
            sx={{ width: 140 }}
          />
          <Button
            variant="outlined"
            startIcon={<GenerateIcon />}
            onClick={() => {
              const id = parseInt(manualRecipeId);
              if (id) generateOne(id);
            }}
            disabled={!manualRecipeId || generating.size > 0}
          >
            Generate
          </Button>
        </Stack>
      </Paper>

      {/* Stats */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`Total: ${stats.total}`} />
          <Chip size="small" color="success" label={`New better: ${stats.new_better}`} />
          <Chip size="small" color="warning" label={`Current better: ${stats.current_better}`} />
          <Chip size="small" color="error" label={`Both bad: ${stats.both_bad}`} />
          <Chip size="small" label={`Unrated: ${stats.unrated}`} />
        </Stack>
      </Paper>

      {/* Test grid */}
      {loading ? (
        <CircularProgress />
      ) : tests.length === 0 ? (
        <Alert severity="info">No tests yet. Use the controls above to generate some.</Alert>
      ) : (
        <Stack spacing={3}>
          {tests.map(t => (
            <Paper key={t.test_id} sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6">{t.recipe_name} <Typography component="span" variant="caption" color="text.secondary">(#{t.recipe_id})</Typography></Typography>
                  {t.qa_passes !== null && (
                    <Chip
                      size="small"
                      color={t.qa_passes ? 'success' : 'error'}
                      label={t.qa_passes ? 'QA passed' : 'QA failed'}
                    />
                  )}
                  {t.photorealism_score !== null && (
                    <Chip size="small" variant="outlined" label={`Real: ${t.photorealism_score}/10`} />
                  )}
                  {t.accuracy_score !== null && (
                    <Chip size="small" variant="outlined" label={`Accurate: ${t.accuracy_score}/10`} />
                  )}
                  {t.appetizing_score !== null && (
                    <Chip size="small" variant="outlined" label={`Appetizing: ${t.appetizing_score}/10`} />
                  )}
                  {t.qa_attempts && t.qa_attempts > 1 && (
                    <Chip size="small" variant="outlined" color="warning" label={`${t.qa_attempts} attempts`} />
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="New (Flux) is better">
                    <IconButton
                      size="small"
                      color={t.rating === 'new_better' ? 'success' : 'default'}
                      onClick={() => handleRate(t.test_id, t.rating === 'new_better' ? null : 'new_better')}
                    >
                      <ThumbUp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Current (DALL-E) is better">
                    <IconButton
                      size="small"
                      color={t.rating === 'current_better' ? 'warning' : 'default'}
                      onClick={() => handleRate(t.test_id, t.rating === 'current_better' ? null : 'current_better')}
                    >
                      <ThumbDown fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Both are bad">
                    <IconButton
                      size="small"
                      color={t.rating === 'both_bad' ? 'error' : 'default'}
                      onClick={() => handleRate(t.test_id, t.rating === 'both_bad' ? null : 'both_bad')}
                    >
                      <ReportProblem fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Re-generate Flux image">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => generateOne(t.recipe_id)}
                        disabled={generating.has(t.recipe_id)}
                      >
                        {generating.has(t.recipe_id) ? <CircularProgress size={16} /> : <GenerateIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(t.test_id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Current (DALL-E)</Typography>
                  {t.current_image_url ? (
                    <img src={t.current_image_url} alt="current" style={{ width: '100%', borderRadius: 4, display: 'block' }} />
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>No current image</Alert>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">New (Flux Pro 1.1)</Typography>
                  <img src={t.new_image_url} alt="new" style={{ width: '100%', borderRadius: 4, display: 'block' }} />
                </Box>
              </Box>
              {t.qa_issues && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                  <Typography variant="caption"><strong>QA flagged:</strong> {t.qa_issues}</Typography>
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontFamily: 'monospace' }}>
                {t.prompt}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ImageTest;
